import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtNum } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileUp } from "lucide-react";

const FIELDS = [
  ["name", "Nom de l'entreprise"], ["website", "Site internet / domaine"], ["country", "Pays"], ["industry", "Secteur"],
  ["linkedin", "LinkedIn"], ["city", "Ville"], ["description", "Description"], ["employees", "Effectif"],
];
const NONE = "__none";
const NEW = "__new";

export default function ImportDialog({ open, onOpenChange, lists, onDone }) {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [listId, setListId] = useState(NEW);
  const [listName, setListName] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setPreview(null); setMapping({}); setListId(NEW); setListName(""); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/imports/preview", fd);
      setPreview(r.data);
      setMapping(r.data.mapping);
      setListName(file.name.replace(/\.csv$/i, ""));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true);
    try {
      const m = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v && v !== NONE));
      await api.post(`/imports/${preview.file_id}/commit`, { mapping: m, list_id: listId === NEW ? null : listId, new_list_name: listName });
      toast.success("Import lancé en arrière-plan");
      onOpenChange(false); reset(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Importer des entreprises</DialogTitle></DialogHeader>
        {!preview ? (
          <label className="border border-dashed border-slate-300 rounded-md py-14 flex flex-col items-center gap-3 cursor-pointer hover:bg-slate-50" data-testid="import-dropzone">
            <FileUp className="h-6 w-6 text-slate-500" />
            <div className="text-sm">{busy ? "Analyse du fichier…" : "Sélectionnez un fichier CSV (séparateur , ; ou tabulation)"}</div>
            <div className="text-xs text-slate-500">Plusieurs milliers de lignes : traitement en arrière-plan, sans bloquer l'interface.</div>
            <input type="file" accept=".csv,.txt" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
          </label>
        ) : (
          <div className="space-y-6">
            <div className="text-sm text-slate-600"><b>{preview.filename}</b> · <span className="tabular">≈ {fmtNum(preview.rows)}</span> lignes · {preview.headers.length} colonnes détectées</div>
            <div>
              <div className="eyebrow mb-3">Correspondance des colonnes</div>
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map(([k, label]) => (
                  <div key={k} className="flex items-center gap-3">
                    <Label className="w-44 text-xs">{label}{k === "name" && " *"}</Label>
                    <Select value={mapping[k] || NONE} onValueChange={(v) => setMapping((m) => ({ ...m, [k]: v }))}>
                      <SelectTrigger className="h-9" data-testid={`map-${k}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value={NONE}>— Ignorer —</SelectItem>{preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Les colonnes non associées sont conservées comme informations complémentaires.</p>
            </div>
            <div>
              <div className="eyebrow mb-2">Aperçu</div>
              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="text-xs w-full"><thead className="bg-slate-50"><tr>{preview.headers.map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>{preview.sample.map((r, i) => <tr key={i} className="border-t border-slate-100">{preview.headers.map((h) => <td key={h} className="px-2 py-1 whitespace-nowrap max-w-[180px] truncate">{r[h]}</td>)}</tr>)}</tbody></table>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Liste de prospection</Label>
                <Select value={listId} onValueChange={setListId}>
                  <SelectTrigger data-testid="import-list-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NEW}>+ Nouvelle liste</SelectItem>{lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {listId === NEW && <div className="space-y-1.5"><Label className="text-xs">Nom de la nouvelle liste</Label><Input value={listName} onChange={(e) => setListName(e.target.value)} data-testid="import-list-name" /></div>}
            </div>
          </div>
        )}
        {preview && (
          <DialogFooter>
            <Button variant="outline" onClick={reset}>Changer de fichier</Button>
            <Button onClick={commit} disabled={busy || !(mapping.name && mapping.name !== NONE) && !(mapping.website && mapping.website !== NONE)} data-testid="import-commit-btn">Lancer l'import</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
